// Pattern-engine orchestrator. Sprint-3 wiring: study classifier → guideline
// selection → methodology audit → numbers → references → voice → keywords.
//
// The orchestrator returns a single PatternRunResult that the side panel
// can render and Sprint 5 can persist to IndexedDB.

import type { ManuscriptDocument } from '../pdf/types';
import { pickGuidelineForStudyType } from '../checklists/loader';
import { classifyStudyType } from './classifier';
import { runMethodology } from './runner';
import { extractNumbers, buildNumberFindings } from './numbers';
import { scanVoice } from './voice';
import { extractReferences, buildReferenceFindings } from './references';
import { extractKeywords } from './keywords';
import type { PatternFinding, PatternRunResult } from './types';

export function runPatternAnalysis(doc: ManuscriptDocument): PatternRunResult {
  const classifier = classifyStudyType(doc);

  let methodology: PatternRunResult['methodology'] = null;
  const findings: PatternFinding[] = [];

  if (!classifier.noFormalGuideline && classifier.label !== 'unclassified') {
    const guideline = pickGuidelineForStudyType(classifier.label);
    if (guideline) {
      const { run, findings: methodFindings } = runMethodology(guideline, doc);
      methodology = run;
      findings.push(...methodFindings);
    }
  }

  const numbers = extractNumbers(doc);
  findings.push(...buildNumberFindings(doc, numbers));

  const references = extractReferences(doc);
  findings.push(...buildReferenceFindings(references));

  findings.push(...scanVoice(doc));

  const keywords = extractKeywords(doc);

  return {
    classifier,
    methodology,
    findings,
    numbers,
    references,
    keywords,
  };
}

export type {
  PatternRunResult,
  PatternFinding,
  ClassifierResult,
  ItemEvidence,
  MethodologyRun,
  NumberMention,
  ReferenceEntry,
  KeywordResult,
  LocatorMatch,
} from './types';

export { classifyStudyType } from './classifier';
export { runMethodology } from './runner';
export { extractNumbers, buildNumberFindings } from './numbers';
export { extractReferences, buildReferenceFindings } from './references';
export { scanVoice } from './voice';
export { extractKeywords } from './keywords';
export { findLocatorMatches } from './locator-engine';
